import React from "react";
import { Box } from "@mui/material";
import { lightTheme } from "../../../styles/theme";
import {
  certificateFontFamily,
  certificateFontKind,
  certificatePaperColour,
  isDarkPaper,
  type CertificateFontId,
  type CertificateOrientation,
  type CertificateTemplate,
} from "../../../domain/certificates/certificateTemplates";
import {
  SIGNATURE_FONT,
  type SignatureInput,
} from "../../../domain/signatures/signatures";

/**
 * The pieces every certificate design is built from: the text a certificate
 * shows, and the organisation mark, title, name, achievement and footer that
 * each design arranges inside its own border.
 */

/** The ink a certificate's text is printed in: dark, or light on a dark paper. */
export function certificateInk(template: Pick<CertificateTemplate, "paperColour">): {
  ink: string;
  muted: string;
} {
  return isDarkPaper(certificatePaperColour(template))
    ? { ink: "#f5f1e6", muted: "rgba(245, 241, 230, 0.72)" }
    : { ink: lightTheme.colors.textDark, muted: lightTheme.colors.secondaryText };
}

/** Everything printed on one certificate, with sample text where nothing's filled in. */
export type CertificateText = {
  certificateTitle: string;
  recipientName: string;
  achievementName: string;
  description: string;
  completionDate: string;
  certificateId: string;
  /** One or two, each drawn as a name over a line and title. */
  signatures: SignatureInput[];
};

export type CertificateLayoutProps = {
  template: CertificateTemplate;
  text: CertificateText;
  /** Every design lays out both ways round. */
  orientation: CertificateOrientation;
};

/** One style's look: its body font and how it lays a certificate out. */
export type CertificateDesign = {
  bodyFont: CertificateFontId;
  Layout: React.FC<CertificateLayoutProps>;
};

/** How a design letters its title and the Awardee's name, where it differs from the template's. */
export type CertificateLettering = {
  titleColour?: string;
  nameColour?: string;
  achievementColour?: string;
  /** A design that always sets the name in its own font, e.g. a script. */
  nameFont?: CertificateFontId;
};

/** The usual arrangement: everything centred in a column inside the design's border. */
export const CentredLayout: React.FC<
  CertificateLayoutProps & {
    /** The border and ornaments, drawn behind the text. */
    frame: React.ReactNode;
    /** CSS inset of the text column inside the frame, for each orientation. */
    inset: Record<CertificateOrientation, string>;
    lettering?: CertificateLettering;
    /** Wraps the title, e.g. in a ribbon. */
    titleWrapper?: (title: React.ReactElement) => React.ReactNode;
    /** An ornament in the space above the footer, e.g. a seal. It shrinks when the text needs the room. */
    centrepiece?: React.ReactNode;
  }
> = ({
  template,
  text,
  orientation,
  frame,
  inset,
  lettering = {},
  titleWrapper = (title) => title,
  centrepiece,
}) => (
  <>
    {frame}
    <Box
      sx={{
        position: "absolute",
        inset: inset[orientation],
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
      }}
    >
      <OrganisationMark template={template} />
      {titleWrapper(
        <Title
          template={template}
          title={text.certificateTitle}
          colour={lettering.titleColour}
        />,
      )}
      <Intro template={template} />
      <RecipientName
        template={template}
        name={text.recipientName}
        align="center"
        colour={lettering.nameColour}
        font={lettering.nameFont}
      />
      <Achievement
        template={template}
        text={text}
        orientation={orientation}
        colour={lettering.achievementColour}
      />
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: centrepiece ? "8px" : 0,
        }}
      >
        {centrepiece}
      </Box>
      <Footer template={template} text={text} orientation={orientation} />
    </Box>
  </>
);

/** How a design lines up its organisation mark and name. */
type TextAlign = "center" | "left";

export const OrganisationMark: React.FC<{
  template: CertificateTemplate;
  align?: TextAlign;
}> = ({ template, align = "center" }) => {
  if (!template.logoUrl && !template.organisationName) return null;
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: align === "left" ? "flex-start" : "center",
        gap: "8px",
        mb: "20px",
      }}
    >
      {template.logoUrl && (
        <Box
          component="img"
          src={template.logoUrl}
          alt=""
          sx={{ maxWidth: 200, maxHeight: 72, objectFit: "contain" }}
        />
      )}
      {template.organisationName && (
        <Box
          sx={{
            fontSize: 18,
            fontWeight: 600,
            letterSpacing: "2px",
            textTransform: "uppercase",
            color: template.primaryColour,
          }}
        >
          {template.organisationName}
        </Box>
      )}
    </Box>
  );
};

/** How a chosen font is lettered on the title and name. */
function lettering(font: CertificateFontId) {
  switch (certificateFontKind(font)) {
    // Script and blackletter ship one weight (a bold would be faked) and read badly in capitals.
    case "script":
      return { title: 64, name: 76, weight: 400, capitals: false, spacing: 0 };
    case "blackletter":
      return { title: 60, name: 68, weight: 400, capitals: false, spacing: 0 };
    // Tall capitals are narrow, so they're set larger.
    case "condensed":
      return { title: 52, name: 64, weight: 600, capitals: true, spacing: "4px" };
    default:
      return { title: 44, name: 60, weight: 700, capitals: true, spacing: "4px" };
  }
}

/**
 * A light title: larger and in plain weight, for designs that want an airy
 * look. Script and blackletter are light already; tall capitals keep their
 * capitals, which are why they were picked.
 */
function lightTitleLettering(font: CertificateFontId) {
  const own = lettering(font);
  switch (certificateFontKind(font)) {
    case "script":
    case "blackletter":
      return own;
    case "condensed":
      return { ...own, title: 56, weight: 400 };
    default:
      return { ...own, title: 60, weight: 400, capitals: false, spacing: 0 };
  }
}

export const Title: React.FC<{
  template: CertificateTemplate;
  title: string;
  light?: boolean;
  colour?: string;
}> = ({ template, title, light = false, colour }) => {
  const letters = light
    ? lightTitleLettering(template.font)
    : lettering(template.font);
  return (
    <Box
      sx={{
        fontFamily: certificateFontFamily(template.font),
        fontSize: letters.title,
        fontWeight: letters.weight,
        lineHeight: 1.15,
        letterSpacing: letters.spacing,
        textTransform: letters.capitals ? "uppercase" : "none",
        color: colour ?? template.primaryColour,
        mb: "18px",
      }}
    >
      {title}
    </Box>
  );
};

export const Intro: React.FC<{ template: CertificateTemplate }> = ({
  template,
}) =>
  template.introText ? (
    <Box
      sx={{
        fontSize: 20,
        fontStyle: "italic",
        color: certificateInk(template).muted,
        mb: "10px",
      }}
    >
      {template.introText}
    </Box>
  ) : null;

export const RecipientName: React.FC<{
  template: CertificateTemplate;
  name: string;
  align?: TextAlign;
  colour?: string;
  font?: CertificateFontId;
}> = ({ template, name, align = "center", colour, font = template.font }) => {
  const letters = lettering(font);
  return (
    <Box
      sx={{
        fontFamily: certificateFontFamily(font),
        fontSize: letters.name,
        fontWeight: letters.weight,
        lineHeight: 1.2,
        color: colour ?? certificateInk(template).ink,
        pb: "10px",
        mb: "20px",
        minWidth: 480,
        borderBottom: `3px solid ${template.accentColour}`,
        textAlign: align,
      }}
    >
      {name}
    </Box>
  );
};

export const Achievement: React.FC<
  CertificateLayoutProps & { colour?: string }
> = ({ template, text, colour }) => (
  <>
    <Box
      sx={{
        fontSize: 26,
        fontWeight: 700,
        color: colour ?? template.primaryColour,
        mb: "8px",
      }}
    >
      {text.achievementName}
    </Box>
    {text.description && (
      <Box
        sx={{
          fontSize: 18,
          lineHeight: 1.5,
          color: certificateInk(template).muted,
          maxWidth: 720,
        }}
      >
        {text.description}
      </Box>
    )}
  </>
);

export const Footer: React.FC<CertificateLayoutProps> = ({
  template,
  text,
}) => (
  <Box sx={{ width: "100%" }}>
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-end",
        gap: "32px",
      }}
    >
      {/* One signature: date left, signature right. Two: signature, date, signature. */}
      {text.signatures.length === 2 && (
        <SignatureBlock template={template} signature={text.signatures[0]} />
      )}
      <SignOffBlock template={template} label="Date">
        <Box sx={{ fontSize: 20, fontWeight: 600, whiteSpace: "nowrap" }}>
          {text.completionDate}
        </Box>
      </SignOffBlock>
      <SignatureBlock
        template={template}
        signature={text.signatures[text.signatures.length - 1]}
      />
    </Box>
    <Box
      sx={{
        mt: "16px",
        fontSize: 13,
        color: certificateInk(template).muted,
        textAlign: "center",
      }}
    >
      Certificate ID: {text.certificateId}
    </Box>
  </Box>
);

const SignatureBlock: React.FC<{
  template: CertificateTemplate;
  signature: SignatureInput;
}> = ({ template, signature }) => (
  <SignOffBlock template={template} label={signature.title}>
    <Box
      sx={{
        fontFamily: certificateFontFamily(SIGNATURE_FONT),
        fontSize: 40,
        lineHeight: 1.1,
        whiteSpace: "nowrap",
      }}
    >
      {signature.name}
    </Box>
  </SignOffBlock>
);

const SignOffBlock: React.FC<{
  template: CertificateTemplate;
  label: string;
  children: React.ReactNode;
}> = ({ template, label, children }) => (
  // Up to 260px wide, narrower when three share a portrait page.
  <Box sx={{ flex: "0 1 260px", minWidth: 0, textAlign: "center" }}>
    <Box
      sx={{
        height: 64,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        pb: "6px",
      }}
    >
      {children}
    </Box>
    <Box
      sx={{
        borderTop: `1px solid ${certificateInk(template).ink}`,
        pt: "8px",
        fontSize: 16,
        fontWeight: 600,
      }}
    >
      {label}
    </Box>
  </Box>
);
